<template>
  <FsForm class="form" :model="form" :rules="rules" ref="formRef">
    <p class="export-project__title">What's included</p>
    <div class="checkbox_group">
      <FsCheckbox v-model="form.containTopologyPic" size="small">Topology</FsCheckbox>
      <FsCheckbox v-model="form.containEquipmentList" size="small">Equipment List</FsCheckbox>
    </div>

    <p class="export-project__divider"></p>
    <p class="export-project__title">Base Info</p>
    <FsFormItem label="Project Name" prop="name">
      <FsInput v-model="form.name" placeholder="Project Name" :maxlength="100" new-regular />
    </FsFormItem>
    <FsFormItem label="Project Area" prop="area">
      <FsSelect
        placeholder="Select project area"
        v-model="form.area"
        filterable
        search-placeholder="Search"
        :options="areaOptions"
        new-regular
      />
    </FsFormItem>
    <FsFormItem label="Project Description" prop="projectDescribe">
      <FsInput
        type="textarea"
        placeholder="Project Description"
        new-regular
        v-model="form.projectDescribe"
        :maxlength="1000"
      />
    </FsFormItem>
    <FsFormItem label="Create By" prop="createBy">
      <FsInput v-model="form.createBy" placeholder="Create By" new-regular :maxlength="30" />
    </FsFormItem>
    <FsFormItem label="Company Name" prop="corporateName">
      <FsInput v-model="form.corporateName" placeholder="Company Name" new-regular :maxlength="50" />
    </FsFormItem>
    <FsFormItem prop="corporateLogo" style="margin-bottom: 0">
      <FsUpload v-model="form.corporateLogo" @change="fileChange" text="Upload Company Logo"></FsUpload>
    </FsFormItem>
  </FsForm>

  <div class="footer_btn">
    <FsButton type="red" size="mediumSmall" @click="exportClick" :loading="loading">Export PDF</FsButton>
    <button type="button" class="cancel_btn" @click.stop="emits('close')">Cancel</button>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { FsForm, FsFormItem, FsButton, FsInput, FsSelect, FsCheckboxGroup, FsCheckbox, FsUpload } from 'fs-design'
import { uploadImage, getCountryList, exportCustomerPdf } from '@/api/modules/project'
import { useWebsiteInfoStore } from '@/store/websiteInfo'
import { storeToRefs } from 'pinia'
import { useProductSelectionStore } from '@/store/productSelection'
import { useUserStore } from '@/store/user'
import { trackEvent } from '@/utils/analytics'
const productSelectionStore = useProductSelectionStore()

interface Props {
  data: Record<string, unknown>
}
interface Option {
  label: string
  value: string
}
const formRef = ref()
const userStore = useUserStore()
const websiteInfo = useWebsiteInfoStore()
const { country_name } = storeToRefs(websiteInfo)
const { customersType } = storeToRefs(userStore)

const emits = defineEmits(['close'])
const props = defineProps<Props>()

const form = ref({
  containTopologyPic: true,
  containEquipmentList: true,
  name: '',
  area: '',
  createBy: '',
  corporateName: '',
  corporateLogo: '',
  projectDescribe: '',
})

const rules = ref({
  name: [{ required: true, message: 'Please input project name' }],
})

const areaOptions = ref<Option[]>([])
const loading = ref(false)

const fileChange = (val: any) => {
  if (val.files.length) {
    const params = new FormData()
    val.files.forEach((item: any) => {
      params.append('file', item)
    })
    uploadImage(params).then((res: any) => {
      form.value.corporateLogo = res.data
    })
  } else {
    form.value.corporateLogo = ''
  }
}

const exportClick = async () => {
  trackEvent({
    eventCategory: 'dec_topology',
    eventAction: 'pdf',
    eventLabel: 'export_pdf',
  })
  try {
    await formRef.value.validate()

    if (loading.value) {
      return
    }

    loading.value = true
    exportCustomerPdf({
      projectId: props.data.id,
      logoUrl: form.value.corporateLogo,
      companyName: form.value.corporateName,
      projectName: form.value.name,
      projectDescribe: form.value.projectDescribe,
      createUerName: form.value.createBy,
      containTopologyPic: form.value.containTopologyPic,
      containEquipmentList: form.value.containEquipmentList,
      projectLocation: form.value.area,
      productIdList: productSelectionStore.selectedProductIds,
    })
      .then((res: any) => {
        trackEvent({
          eventCategory: 'dec_topology',
          eventAction: 'pdf',
          eventLabel: 'pdf_generated',
        })
        for (const key in res.headers) {
          if (res.headers.hasOwnProperty(key)) {
            console.log(`${key}: ${res.headers[key]}`)
          }
        }
        const contentDisposition = res?.headers['content-disposition']
        const match = contentDisposition?.match(/filename="?(.+?)"?$/)
        const filename = match ? decodeURIComponent(match[1]) : 'download.pdf'

        const blob = new Blob([res?.data], { type: 'application/pdf' })
        const blobUrl = URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        URL.revokeObjectURL(blobUrl)
        link.remove()
        emits('close')
        // loading.value = false
      })
      .catch(err => {
        trackEvent({
          eventCategory: 'dec_topology',
          eventAction: 'error',
          eventLabel: err,
        })
        loading.value = false
      })
  } catch (e) {
    console.log(e)
  }
}
const getCountry = () => {
  getCountryList().then((res: any) => {
    res.data.forEach((item: any) => {
      areaOptions.value.push({
        label: item.countryEn,
        value: item.countryEn,
      })
    })
  })
}

onMounted(() => {
  getCountry()
})

watch(
  () => props.data,
  newVal => {
    form.value.name = newVal.name as string
    form.value.area = newVal.area as string
    form.value.createBy = (newVal.createdBy as string) || ''
    form.value.projectDescribe = (newVal.description as string) || (newVal.templateDescription as string)
    console.log('__112323323')
    console.log(form.value)
  }
)
</script>

<style lang="scss" scoped>
.form {
  padding: 16px 20px 20px 20px;
  flex: 1 1 auto;
  overflow: auto;

  :deep(.fs-upload) {
    .fs-upload__wrap {
      .fs-upload__box {
        .fs-button--gray.is-plain {
          background: transparent;
          color: #19191a;
        }
      }
    }
  }
}

.checkbox_group {
}

.footer_btn {
  padding: 20px 20px 24px 20px;
  flex-shrink: 0;
  display: flex;
  flex-direction: row-reverse;

  .cancel_btn {
    @include textButton();
    margin-right: 16px;
  }
}
</style>
